import { makeStyles } from '@material-ui/core/styles';
import { NoSsr } from '@material-ui/core';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { CatalogueProduct } from '../../../api/products/catalogue';
import { isAuthenticatedState } from '../../../api/appState';
import { activeFavoriteProducts, showStockAvailability } from '../../../config';
import { isProductCanceled } from '../../../helpers/isProductCanceled';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ProductDetailFavoriteToggle from '../../ProductDetailPage/components/ProductDetailFavoriteToggle';
import CataloguePageListContentItemCardAddToCart from './CataloguePageListContentItemCardAddToCart';
// import CataloguePageListContentItemCardDescription from './CataloguePageListContentItemCardDescription';
import CataloguePageListContentItemCardImage from './CataloguePageListContentItemCardImage';
import CataloguePageListContentItemCardInlineParameters from './CataloguePageListContentItemCardInlineParameters';
import CataloguePageListContentItemCardName from './CataloguePageListContentItemCardName';
import CataloguePageListContentItemCardPackageInfo from './CataloguePageListContentItemCardPackageInfo';
import CataloguePageListContentItemCardPrice from './CataloguePageListContentItemCardPrice';
import CataloguePageListContentItemCardStockInfo from './CataloguePageListContentItemCardStockInfo';
import CataloguePageListContentItemWarning from './CataloguePageListContentItemWarning';

export interface CataloguePageListContentItemCardProps
  extends StandardDivProps<CataloguePageListContentItemCardClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemCardClassKey = 'root';

const themeSettings = { name: 'CataloguePageListContentItemCard' };
const useStyles = makeStyles(
  (theme) => ({
    root: {
      gridGap: 0,
      display: 'grid',
      minWidth: 286,
      maxWidth: '100%',
      flexBasis: '100%',
      flexGrow: 0,
      position: 'relative',
      flexShrink: 1,
      gridTemplateAreas: `
      "code         code"
      "image        image"
      "name         name"
      "price        stockInfo"
      "addToCart    addToCart"
      "parameters   parameters"
      "variants     variants"
      `,
      gridTemplateRows: '20px auto 58px 45px 45px 20px auto',
      gridTemplateColumns: '1fr 1fr',
      padding: theme.spacing(1),
      margin: theme.spacing(1),
      border: '1px solid',
      borderColor: theme.palette.divider,
      borderRadius: theme.spacing(0.5),
      '@media(min-width: 650px)': {
        maxWidth: 'calc(50% - 16px)',
        flexBasis: 'calc(50% - 16px)',
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: 'calc(100% - 16px)',
        padding: theme.spacing(2),
      },
      '@media(min-width: 1700px)': {
        minWidth: 234,
        maxWidth: 'calc(25% - 16px)',
        flexBasis: '25%',
        flexGrow: 1,
        flexShrink: 1,
      },
      '@media(min-width: 1990px)': {
        maxWidth: 'calc(25% - 16px)',
      },
      '@media(min-width: 2330px)': {
        maxWidth: 'calc(20% - 16px)',
        minWidth: 'calc(20% - 16px)',
      },
    },
    image: {
      gridArea: 'image',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 1 / 2,
      },
      margin: 'auto',
    },
    productName: {
      gridArea: 'name',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 3,
        '-ms-grid-column': 1 / 2,
      },
    },
    description: {
      gridArea: 'description',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 4,
        '-ms-grid-column': 1 / 2,
      },
    },
    price: {
      gridArea: 'price',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 6,
        '-ms-grid-column': 1,
      },
    },
    code: {
      gridArea: 'code',
      ...theme.typography.caption,
      color: theme.palette.text.secondary,
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1,
      },
    },
    codeLable: {
      paddingRight: '1ch',
      color: theme.palette.text.hint,
    },
    parameters: {
      gridArea: 'parameters',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1,
      },
    },
    addToCart: {
      gridArea: 'addToCart',
    },
    stockInfo: {
      gridArea: 'stockInfo',
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 5,
        '-ms-grid-column': 2,
      },
    },
    variants: {
      gridArea: 'variants',
      marginTop: '1rem',
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 5,
        '-ms-grid-column': 1,
      },
    },
    packageInfo: {
      position: 'absolute',
      top: '0',
      right: '0',
      width: 'auto',
      padding: '1rem',
    },
    favoriteToggle: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 1,
    },
  }),
  themeSettings,
);

const CataloguePageListContentItemCard = React.forwardRef<HTMLDivElement, CataloguePageListContentItemCardProps>(
  ({ className, classes: pClasses, product }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const isAuthenticated = useRecoilValue(isAuthenticatedState);
    const t = useTranslateWithFallback();

    const productIsCanceled = isProductCanceled(product.taxonomies);

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <CataloguePageListContentItemCardImage className={classes.image} product={product} />
        {activeFavoriteProducts && isAuthenticated && (
          <NoSsr>
            <ProductDetailFavoriteToggle className={classes.favoriteToggle} productId={product.id} />
          </NoSsr>
        )}
        <CataloguePageListContentItemCardName className={classes.productName} product={product} />
        <CataloguePageListContentItemCardPackageInfo product={product} />
        {/* <CataloguePageListContentItemCardDescription className={classes.description} product={product} /> */}
        <div className={classes.code}>
          <span className={classes.codeLable}>{t('common/code')}:</span>
          <span>{product.sku || ''}</span>
        </div>
        <CataloguePageListContentItemCardInlineParameters className={classes.parameters} product={product} />
        {productIsCanceled === false && (
          <>
            {isAuthenticated && <CataloguePageListContentItemCardPrice className={classes.price} product={product} />}
            <CataloguePageListContentItemWarning className={classes.variants} product={product} type='grid' />
            {showStockAvailability && isAuthenticated && (
              <CataloguePageListContentItemCardStockInfo className={classes.stockInfo} product={product} />
            )}
            {isAuthenticated && (
              <CataloguePageListContentItemCardAddToCart className={classes.addToCart} product={product} />
            )}
          </>
        )}
      </div>
    );
  },
);

CataloguePageListContentItemCard.displayName = 'CataloguePageListContentItemCard';

export default withThemeProps(themeSettings)(CataloguePageListContentItemCard);
